import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import ErrorBoundary from "@/components/ErrorBoundary";
const Index = lazy(() => import("./pages/Index"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AccessibilityWidget = lazy(() => import("@/components/AccessibilityWidget"));
import MetaPixelTracker from "@/components/MetaPixelTracker";
import GA4Tracker from "@/components/GA4Tracker";
import PostHogTracker from "@/components/PostHogTracker";
import CTATracker from "@/components/CTATracker";
import CookieConsent from "@/components/CookieConsent";
import PreferredSourceNudge from "@/components/PreferredSourceNudge";
import { hasConsent, subscribeConsent } from "@/lib/cookieConsent";
import { Navigate } from "react-router-dom";

// Lazy load non-critical routes
const TermsPage = lazy(() => import("./pages/TermsPage"));
const IntakePage = lazy(() => import("./pages/IntakePage"));
const RepairsIndex = lazy(() => import("./pages/RepairsIndex"));
const RepairPage = lazy(() => import("./pages/RepairPage"));
const ModelPage = lazy(() => import("./pages/ModelPage"));
const BrandPage = lazy(() => import("./pages/BrandPage"));
const CityServicePage = lazy(() => import("./pages/CityServicePage"));
const ModelCityPage = lazy(() => import("./pages/ModelCityPage"));
const AccessibilityPage = lazy(() => import("./pages/AccessibilityPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
const ReviewToolPage = lazy(() => import("./pages/ReviewToolPage"));
const NeighborhoodServicePage = lazy(() => import("./pages/NeighborhoodServicePage"));
const GuidesIndex = lazy(() => import("./pages/GuidesIndex"));
const GuidePage = lazy(() => import("./pages/GuidePage"));
const FAQPage = lazy(() => import("./pages/FAQPage"));
const InfoIndex = lazy(() => import("./pages/InfoIndex"));
const InfoArticlePage = lazy(() => import("./pages/InfoArticlePage"));
const ServiceArticlePage = lazy(() => import("./pages/ServiceArticlePage"));
const SeriesPage = lazy(() => import("./pages/SeriesPage"));
const GeoBrandPage = lazy(() => import("./pages/GeoBrandPage"));
const TrustPage = lazy(() => import("./pages/TrustPage"));
const UrgentRepairPage = lazy(() => import("./pages/UrgentRepairPage"));
const BusinessRepairPage = lazy(() => import("./pages/BusinessRepairPage"));
// English mirror (/en) — separate lightweight pages, loaded only for English visitors.
const EnHome = lazy(() => import("./pages/en/EnHome"));
const EnRepairs = lazy(() => import("./pages/en/EnRepairs"));
const EnModelPage = lazy(() => import("./pages/en/EnModelPage"));
const EnAbout = lazy(() => import("./pages/en/EnAbout"));
const EnContact = lazy(() => import("./pages/en/EnContact"));
const EnFaq = lazy(() => import("./pages/en/EnFaq"));
const LocalizedSite = lazy(() => import("./pages/localized/LocalizedSite"));
import { useLocation } from "react-router-dom";
import { manifest, retiredTarget } from "@/i18n/lang";
import InternationalNotFound from "@/components/InternationalNotFound";
function RetiredCityRedirect() {
  const { brand, model } = useParams();
  const location = useLocation();
  const target = model && brand ? `/repair/${brand}/${model}/jerusalem` : brand ? `/local/${brand}/jerusalem` : "/city/jerusalem";
  return <Navigate to={`${target}${location.search}${location.hash}`} replace />;
}
function RetiredLanguageRedirect() { const location = useLocation(); return <Navigate to={`${retiredTarget(location.pathname, manifest) || "/en"}${location.search}${location.hash}`} replace />; }

const queryClient = new QueryClient();
const META_PIXEL_ID = "1441145443958666";

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
    _fbq?: (...args: any[]) => void;
    __metaPixelInitialized?: boolean;
  }
}

// Removed blocking PageLoader - pages render instantly
const PageLoader = () => null;

const App = () => {
  useEffect(() => {
    const sync = () => {
    if (!hasConsent("marketing")) { window.fbq?.("consent", "revoke"); return; }
    if (window.__metaPixelInitialized) { window.fbq?.("consent", "grant"); return; }
    window.__metaPixelInitialized = true;

    import("react-meta-pixel")
      .then((module) => {
        if (!hasConsent("marketing")) { window.__metaPixelInitialized = false; return; }
        const { FacebookPixel } = module;
        const pixel = new FacebookPixel({ pixelID: META_PIXEL_ID, debug: false, pageViewOnInit: true });
        pixel.init({});
      })
      .catch(() => {
        if (!hasConsent("marketing")) { window.__metaPixelInitialized = false; return; }
        if (window.fbq) return;

        (function (f: Window, b: Document, e: string, v: string) {
          let n: any, t: HTMLScriptElement, s: Element;
          if (f.fbq) return;
          n = f.fbq = function (...args: any[]) {
            n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
          };
          if (!f._fbq) f._fbq = n;
          n.push = n;
          n.loaded = true;
          n.version = "2.0";
          n.queue = [];
          t = b.createElement(e) as HTMLScriptElement;
          t.async = true;
          t.src = v;
          s = b.getElementsByTagName(e)[0];
          s.parentNode?.insertBefore(t, s);
        })(window, document, "script", "https://connect.facebook.net/en_US/fbevents.js");

        window.fbq?.("init", META_PIXEL_ID);
        window.fbq?.("track", "PageView");
      });
    };
    sync();
    return subscribeConsent(sync);
  }, []);

  return (
    <ErrorBoundary>
      <HelmetProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <BrowserRouter>
              <MetaPixelTracker />
              <GA4Tracker />
              <PostHogTracker />
              <CTATracker />
              <Suspense fallback={null}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/index" element={<Navigate to="/" replace />} />
                  <Route path="/index.html" element={<Navigate to="/" replace />} />
                  <Route path="/terms" element={<TermsOfServicePage />} />
                  <Route path="/terms-of-use" element={<Navigate to="/terms" replace />} />
                  <Route path="/accessibility" element={<AccessibilityPage />} />
                  <Route path="/intake" element={<IntakePage />} />
                  <Route path="/repairs" element={<RepairsIndex />} />
                  <Route path="/repairs/:slug" element={<RepairPage />} />
                  <Route path="/city/tzur-hadassah" element={<RetiredCityRedirect />} />
                  <Route path="/local/:brand/tzur-hadassah" element={<RetiredCityRedirect />} />
                  <Route path="/repair/:brand/:model/tzur-hadassah" element={<RetiredCityRedirect />} />
                  <Route path="/repair/:brand" element={<BrandPage />} />
                  <Route path="/repair/:brand/:model" element={<ModelPage />} />
                  <Route path="/repair/:brand/:model/:citySlug" element={<ModelCityPage />} />
                  {/* Neighborhood × Service SAB pages — separate /area/ namespace to avoid clash with /repair/:brand/:model */}
                  <Route path="/area/:service/:neighborhood" element={<NeighborhoodServicePage />} />
                  <Route path="/city/:citySlug" element={<CityServicePage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/faq" element={<FAQPage />} />
                  <Route path="/privacy" element={<PrivacyPage />} />
                  <Route path="/privacy-policy" element={<Navigate to="/privacy" replace />} />
                  <Route path="/review" element={<ReviewToolPage />} />
                  <Route path="/guides" element={<GuidesIndex />} />
                  <Route path="/guides/:slug" element={<GuidePage />} />
                  <Route path="/info" element={<InfoIndex />} />
                  <Route path="/info/:slug" element={<InfoArticlePage />} />
                  <Route path="/services/:slug" element={<ServiceArticlePage />} />
                  <Route path="/services/screen-replacement-service" element={<Navigate to="/services/screen-replacement-at-home" replace />} />
                  <Route path="/services/same-day-phone-repair" element={<Navigate to="/urgent" replace />} />
                  <Route path="/series/:slug" element={<SeriesPage />} />
                  <Route path="/local/:brand/:city" element={<GeoBrandPage />} />
                   <Route path="/trust" element={<TrustPage />} />
                  <Route path="/urgent" element={<UrgentRepairPage />} />
                  <Route path="/business" element={<BusinessRepairPage />} />

                  {/* English mirror */}
                  <Route path="/en" element={<EnHome />} />
                  <Route path="/en/repairs" element={<EnRepairs />} />
                  <Route path="/en/repair/:brand/:model" element={<EnModelPage />} />
                  <Route path="/en/about" element={<EnAbout />} />
                  <Route path="/en/contact" element={<EnContact />} />
                  <Route path="/en/faq" element={<EnFaq />} />
                  <Route path="/en/*" element={<InternationalNotFound lang="en" />} />

                  {/* Maintained Russian section and retired-language redirects */}
                  <Route path="/ru/*" element={<LocalizedSite />} />
                  <Route path="/hi/*" element={<LocalizedSite />} />
                  <Route path="/si/*" element={<LocalizedSite />} />
                  <Route path="/ta/*" element={<LocalizedSite />} />
                  <Route path="/th/*" element={<LocalizedSite />} />

                  <Route path="*" element={<NotFound />} />

                </Routes>
              </Suspense>
              <Suspense fallback={null}>
                <AccessibilityWidget />
              </Suspense>
              <PreferredSourceNudge />
              <CookieConsent />
            </BrowserRouter>
          </TooltipProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default App;
